// Enhanced Token Storage with persistent memory

interface UserData {
  user_id: number;
  user_mobileno: string;
  otp: string;
  user_fname: string;
  user_lname: string;
  goal: string;
  gender: string;
  age: number;
  city: string;
  state: string;
  dob: string;
  height_parameter: string;
  height_value: number;
  weight_parameter: string;
  weight_value: number;
  target_weight: number;
  target_weight_parameter: string;
  food_preference: string;
  progress: string;
  medical_conditions: string;
  last_status: string | null;
  profile_url: string;
}

interface AuthState {
  access_token: string;
  refresh_token: string;
  user_id: number | null;
  userData: UserData | null;
  isAuthenticated: boolean;
  lastLoginTime: number;
}

// Create a private state variable
const authState: AuthState = {
  access_token: '',
  refresh_token: '',
  user_id: null,
  userData: null,
  isAuthenticated: false,
  lastLoginTime: 0
};

export const AuthStorage = {
  // Session timeout (24 hours in milliseconds)
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000,

  setAuthData: (
    accessToken: string, 
    refreshToken: string, 
    userId: number, 
    userData: UserData | null = null
  ) => {
    const now = Date.now();
    
    authState.access_token = accessToken;
    authState.refresh_token = refreshToken;
    authState.user_id = userId;
    authState.userData = userData;
    authState.isAuthenticated = true;
    authState.lastLoginTime = now;
    
    console.log('Auth data stored:', {
      userId,
      hasUserData: !!userData,
      loginTime: new Date(now).toISOString()
    });
  },

  setUserData: (userData: UserData) => {
    authState.userData = userData;
    console.log('User data updated:', userData);
  },

  getAccessToken: (): string => {
    if (!AuthStorage.isSessionValid()) {
      return '';
    }
    return authState.access_token;
  },

  getRefreshToken: (): string => {
    if (!AuthStorage.isSessionValid()) {
      return '';
    }
    return authState.refresh_token;
  },

  getUserId: (): number | null => {
    if (!AuthStorage.isSessionValid()) {
      return null;
    }
    return authState.user_id;
  },

  getUserData: (): UserData | null => {
    if (!AuthStorage.isSessionValid()) {
      return null;
    }
    return authState.userData;
  },

  isAuthenticated: (): boolean => {
    return AuthStorage.isSessionValid() && authState.isAuthenticated;
  },

  isSessionValid: (): boolean => {
    if (!authState.isAuthenticated || !authState.lastLoginTime) {
      return false;
    }
    
    const now = Date.now();
    const sessionAge = now - authState.lastLoginTime;
    
    if (sessionAge > AuthStorage.SESSION_TIMEOUT) {
      console.log('Session expired, clearing auth data');
      AuthStorage.clearAuth();
      return false;
    }
    
    return true;
  },

  clearAuth: () => {
    authState.access_token = '';
    authState.refresh_token = '';
    authState.user_id = null;
    authState.userData = null;
    authState.isAuthenticated = false;
    authState.lastLoginTime = 0;
    console.log('Auth data cleared');
  },

  // Check if user has completed registration (has name)
  hasCompletedRegistration: (): boolean => {
    const userData = AuthStorage.getUserData();
    return !!(userData?.user_fname && userData.user_fname.trim().length > 0);
  },

  // Get user's last status to determine flow
  getUserLastStatus: (): string | null => {
    const userData = AuthStorage.getUserData();
    return userData?.last_status || null;
  },

  // Debug info
  getDebugInfo: () => {
    const sessionAge = authState.lastLoginTime 
      ? Date.now() - authState.lastLoginTime 
      : 0;
    
    return {
      isAuthenticated: authState.isAuthenticated,
      hasUserId: !!authState.user_id,
      hasUserData: !!authState.userData,
      hasTokens: !!(authState.access_token && authState.refresh_token),
      sessionAge: Math.floor(sessionAge / 1000), // in seconds
      sessionValid: AuthStorage.isSessionValid(),
      lastLoginTime: authState.lastLoginTime 
        ? new Date(authState.lastLoginTime).toISOString() 
        : null
    };
  }
};

// Export for backward compatibility with existing Login_Form
export const TokenStorage = AuthStorage;